import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import CloseIcon from '../assets/images/close-big-15x15.svg';
import CloseSmallIcon from '../assets/images/close-small-15x15.svg';
import SearchIcon from '../assets/images/search-15x15.svg';
import ArrowRightMiddleIcon from '../assets/images/arrow-right-middle-15x15.svg';
import ArrowLeftMiddleIcon from '../assets/images/arrow-left-middle-15x15.svg';
import ArrowDoubleRightMiddleIcon from '../assets/images/arrow-double-right-middle-15x15.svg';
import ArrowDoubleLeftMiddleIcon from '../assets/images/arrow-double-left-middle-15x15.svg';
import CheckedIcon from '../assets/images/checked-15x15.svg';
import UncheckedIcon from '../assets/images/unchecked-15x15.svg';
import Button from '../components/Button';
import ButtonGroup from '../components/ButtonGroup';
import Select from '../components/Select';
import { __ } from '../core/utils';
import { setNotification } from '../redux/actions/general/notification';
import {
  linkFilesToModules,
  listFileLinkTargets,
} from '../redux/actions/table/files';
import { attachmentCategoryOptions } from '../helpers/attachmentCategories';
import styles from '../styles/wizards/LinkFileToModelWizard.scss';

const MODEL_TYPES = ['Task', 'Tender', 'Specification', 'Plan', 'Product'];
const CATEGORIZED_MODELS = ['Specification', 'Product'];

const LinkFileToModelWizard = ({
  stage_id,
  file,
  files = [],
  file_ids = [],
  onClose,
  onLinked,
}) => {
  const [modelType, setModelType] = useState('Task');
  const [targets, setTargets] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [activeSelect, setActiveSelect] = useState(null);
  const requestId = useRef(0);
  const searchTimeout = useRef(null);
  const selectedFileIds = !_.isEmpty(file_ids)
    ? file_ids
    : _.compact([file && file.id]);
  const selectedFiles = !_.isEmpty(files) ? files : _.compact([file]);
  const primaryFile = file || _.head(selectedFiles) || {};
  const primaryFileId = primaryFile.id;
  const multipleFiles = _.size(selectedFileIds) > 1;
  const visiblePagesLimit = 5;
  const totalPages = parseInt(pages);
  const currentPage = parseInt(page);
  let startPage;
  let endPage;

  if (totalPages <= visiblePagesLimit) {
    startPage = 1;
    endPage = totalPages;
  } else if (currentPage <= visiblePagesLimit - 1) {
    startPage = 1;
    endPage = visiblePagesLimit;
  } else if (currentPage + 2 >= totalPages) {
    startPage = totalPages - visiblePagesLimit + 1;
    endPage = totalPages;
  } else {
    startPage = currentPage - 3;
    endPage = currentPage + 2;
  }

  useEffect(() => {
    const currentRequestId = ++requestId.current;
    let active = true;

    setLoading(true);

    listFileLinkTargets(
      stage_id,
      modelType,
      _.includes(CATEGORIZED_MODELS, modelType) || multipleFiles
        ? undefined
        : primaryFileId,
      {
        pagination: 1,
        limit: 30,
        page,
        query: query || undefined,
      },
    ).then(
      ({ response }) => {
        if (!active || currentRequestId !== requestId.current) {
          return;
        }

        setTargets(_.get(response, 'data', []));
        setPages(Math.max(parseInt(_.get(response, 'pagination.pages', 1)), 1));
        setLoading(false);
      },
      () => {
        if (active && currentRequestId === requestId.current) {
          setLoading(false);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [stage_id, primaryFileId, multipleFiles, modelType, page, query]);

  useEffect(
    () => () => {
      clearTimeout(searchTimeout.current);
    },
    [],
  );

  const changeModelType = (value) => {
    setModelType(value);
    setSearchValue('');
    setQuery('');
    setPage(1);
  };

  const search = (event) => {
    const value = event.target.value;

    setSearchValue(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setQuery(_.trim(value));
      setPage(1);
    }, 300);
  };

  const targetKey = (model_type, model_id) => model_type + ':' + model_id;

  const isSelected = (target) =>
    _.some(
      selectedTargets,
      (selected) =>
        targetKey(selected.model_type, selected.model_id) ===
        targetKey(modelType, target.id),
    );

  const toggleTarget = (target) => {
    const key = targetKey(modelType, target.id);

    setSelectedTargets((current) => {
      if (
        _.some(
          current,
          (selected) =>
            targetKey(selected.model_type, selected.model_id) === key,
        )
      ) {
        return _.reject(
          current,
          (selected) =>
            targetKey(selected.model_type, selected.model_id) === key,
        );
      }

      return [
        ...current,
        {
          ...target,
          model_type: modelType,
          model_id: target.id,
          category: _.includes(CATEGORIZED_MODELS, modelType) ? '' : null,
        },
      ];
    });
  };

  const removeTarget = (target) => {
    const key = targetKey(target.model_type, target.model_id);

    setSelectedTargets((current) =>
      _.reject(
        current,
        (selected) => targetKey(selected.model_type, selected.model_id) === key,
      ),
    );
  };

  const changeCategory = (target, category) => {
    const key = targetKey(target.model_type, target.model_id);

    setSelectedTargets((current) =>
      _.map(current, (selected) =>
        targetKey(selected.model_type, selected.model_id) === key
          ? { ...selected, category }
          : selected,
      ),
    );
  };

  const canLink =
    !_.isEmpty(selectedFileIds) &&
    !_.isEmpty(selectedTargets) &&
    _.every(
      selectedTargets,
      (target) =>
        !_.includes(CATEGORIZED_MODELS, target.model_type) || target.category,
    );
  const moduleOptions = _.map(MODEL_TYPES, (value) => ({
    value,
    label: __(value),
  }));
  const categoryOptions = attachmentCategoryOptions(primaryFile.extension);

  const link = () => {
    if (!canLink || linking) {
      return;
    }

    setLinking(true);

    linkFilesToModules(stage_id, selectedFileIds, selectedTargets)
      .then(() => Promise.resolve(onLinked && onLinked()))
      .then(() => {
        setNotification(__('files.wizard.file-linked'));
        onClose();
      })
      .catch(() => setLinking(false));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <span>{__('files.flyout.link-to', {}, 'Link to')}</span>
          <small>
            {multipleFiles
              ? __(
                  'files.link-wizard.files-selected',
                  { COUNT: _.size(selectedFileIds) },
                  _.size(selectedFileIds) + ' files selected',
                )
              : primaryFile.file_name}
          </small>
        </div>
        <CloseIcon onClick={onClose} />
      </div>

      <div className={styles.toolbar}>
        <div className={styles.moduleRow}>
          <div className={styles.moduleSelect}>
            <Select
              label={__('files.link-wizard.module')}
              input={{
                name: 'link_module',
                value: modelType,
                onChange: changeModelType,
                onFocus: () => setActiveSelect('module'),
                onBlur: () => setActiveSelect(null),
              }}
              meta={{
                active: activeSelect === 'module',
                form: 'link_files_to_modules',
              }}
              options={moduleOptions}
              noSort
            />
          </div>
        </div>
        <label className={styles.search}>
          <SearchIcon />
          <input
            value={searchValue}
            onChange={search}
            placeholder={__('files.link-wizard.search')}
          />
        </label>
      </div>

      <div className={styles.selection}>
        <div className={styles.selectionHeader}>
          <span>{__('files.link-wizard.selected-items')}</span>
          <strong>{selectedTargets.length}</strong>
        </div>
        {_.isEmpty(selectedTargets) ? (
          <div className={styles.emptySelection}>
            {__('files.link-wizard.select-item-help')}
          </div>
        ) : (
          <div className={styles.selectedItems}>
            {_.map(selectedTargets, (target) => (
              <div
                className={styles.selectedItem}
                key={targetKey(target.model_type, target.model_id)}
              >
                <div className={styles.selectedItemInfo}>
                  <small>{__(target.model_type)}</small>
                  <strong>
                    {_.filter([target.code, target.title]).join(': ') || '—'}
                  </strong>
                </div>
                {_.includes(CATEGORIZED_MODELS, target.model_type) && (
                  <div className={styles.categorySelect}>
                    <Select
                      label={__('files.link-wizard.category', {}, 'Category')}
                      placeholder={__(
                        'files.link-wizard.select-category',
                        {},
                        'Select category',
                      )}
                      input={{
                        name:
                          'link_category_' +
                          targetKey(target.model_type, target.model_id),
                        value: target.category,
                        onChange: (category) =>
                          changeCategory(target, category),
                        onFocus: () =>
                          setActiveSelect(
                            targetKey(target.model_type, target.model_id),
                          ),
                        onBlur: () => setActiveSelect(null),
                      }}
                      meta={{
                        active:
                          activeSelect ===
                          targetKey(target.model_type, target.model_id),
                        form: 'link_files_to_modules',
                      }}
                      options={categoryOptions}
                      searchable
                      noSort
                    />
                  </div>
                )}
                <button
                  type='button'
                  className={styles.removeTarget}
                  onClick={() => removeTarget(target)}
                >
                  <CloseSmallIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.results}>
          <div className={styles.resultsHeader}>
            {__('files.link-wizard.available-items')}
          </div>
          <div className={styles.resultRows}>
            {loading && (
              <div className={styles.empty}>{__('general.loading')}</div>
            )}
            {!loading && _.isEmpty(targets) && (
              <div className={styles.empty}>
                {__('files.link-wizard.no-items')}
              </div>
            )}
            {!loading &&
              _.map(targets, (target) => {
                const selected = isSelected(target);

                return (
                  <div
                    key={targetKey(modelType, target.id)}
                    className={classNames(
                      styles.resultRow,
                      selected && styles.selected,
                    )}
                    onClick={() => toggleTarget(target)}
                  >
                    {selected ? (
                      <CheckedIcon className={styles.checked} />
                    ) : (
                      <UncheckedIcon className={styles.unchecked} />
                    )}
                    <div className={styles.targetLabel}>
                      {target.code && (
                        <strong>
                          {target.code + (target.title ? ':' : '')}
                        </strong>
                      )}
                      <span>{target.title || (!target.code && '—')}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.pagination}>
          <button
            className={styles.paginationArrow}
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            <ArrowDoubleLeftMiddleIcon />
          </button>
          <button
            className={styles.paginationArrow}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <ArrowLeftMiddleIcon />
          </button>
          {_.map(_.range(startPage, endPage + 1), (paginationPage) => (
            <span
              onClick={() => setPage(paginationPage)}
              key={paginationPage}
              className={classNames(
                styles.page,
                page == paginationPage && styles.active,
              )}
            >
              {_.padStart(paginationPage, 2, '0')}
            </span>
          ))}
          <button
            className={styles.paginationArrow}
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            <ArrowRightMiddleIcon />
          </button>
          <button
            className={styles.paginationArrow}
            disabled={page >= pages}
            onClick={() => setPage(totalPages)}
          >
            <ArrowDoubleRightMiddleIcon />
          </button>
        </div>
        <div className={styles.actions}>
          <Button
            lightGray
            medium
            left
            middleText={__('button.cancel')}
            onClick={onClose}
          />
          <ButtonGroup>
            <Button
              lightBlue
              medium
              middleText={__('files.flyout.link-to')}
              spinning={linking}
              disabled={!canLink || linking}
              onClick={link}
            />
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};

export default LinkFileToModelWizard;
